"use client";

import type React from "react";
import { useState, useCallback, useRef, useEffect } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Menu, X, LogOut, Globe, Gamepad2,
  Radio, User, Swords, Trophy, Rows4, Layers3, Shield,
  CalendarDays, ChevronRight, ChevronDown, Bell, Megaphone,
  GraduationCap, Layers,
  Gift, HelpCircle, Brain, Dumbbell, Target, BarChart2,
  CheckSquare, Grid3X3, ShieldQuestion, Sparkles, Settings2, BookOpen, Flag,
  ImageIcon, ClipboardCheck,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { clearAllTokens } from "@/utils/storage";
import { useQueryClient } from "@tanstack/react-query";
import { useQuizTypesAdmin } from "@/hooks/api/useQuizTypes";

// Lucide icon name → component (for API-driven menu items)
const ICON_MAP: Record<string, React.ElementType> = {
  HelpCircle, User, Dumbbell, BarChart2, ShieldQuestion, CheckSquare,
  Grid3X3, Brain, Target, Shield, Trophy, Gamepad2, Sparkles,
};

// ─── Types ────────────────────────────────────────────────────────────────────

interface NavItem {
  name: string;
  href: string;
  icon: React.ElementType;
  /** When true, render as <a target="_blank"> instead of React Router <Link> */
  external?: boolean;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

interface ModeGroup {
  id: string;
  label: string;
  icon: React.ElementType;
  /** Tailwind bg + text color classes for the icon dot */
  color: string;
  status?: "coming-soon";
  items: NavItem[];
}

// ─── Static nav (always visible, non-collapsible) ─────────────────────────────

const staticGroups: NavGroup[] = [
  {
    label: "Overview",
    items: [{ name: "Dashboard", href: "/", icon: LayoutDashboard }],
  },
  {
    label: "App",
    items: [
      { name: "Modes", href: "/modes", icon: Gamepad2 },
      { name: "Players", href: "/players", icon: User },
      { name: "Clubs", href: "/clubs", icon: Shield },
      { name: "Announcements", href: "/announcements", icon: Megaphone },
      { name: "Stories", href: "/stories", icon: BookOpen },
      { name: "Social Cards", href: "/social-cards", icon: ImageIcon },
      { name: "Push Notifications", href: "/notifications", icon: Bell },
    ],
  },
  {
    label: "Users",
    items: [
      { name: "Users", href: "/users", icon: Users },
      { name: "Abuse Reports", href: "/reports/abuse", icon: Flag },
    ],
  },
  {
    label: "Help",
    items: [
      { name: "Admin Guide", href: "/admin-guide.html", icon: BookOpen, external: true },
    ],
  },
];

// ─── Mode groups (collapsible, one per game mode) ─────────────────────────────
// To add a new mode in future: push one more ModeGroup object here.

const modeGroups: ModeGroup[] = [
  {
    id: "fifa-world-cup",
    label: "FIFA World Cup 2026",
    icon: Trophy,
    color: "bg-amber-500",
    items: [
      { name: "Nations", href: "/nations", icon: Globe },
      { name: "Phases", href: "/phases", icon: Rows4 },
      { name: "Matches", href: "/matches", icon: Swords },
      { name: "Gameweeks", href: "/gameweeks", icon: CalendarDays },
      { name: "Team of Week", href: "/gameweeks/team-of-the-week", icon: Sparkles },
      { name: "Live Scorer", href: "/live-scorer", icon: Radio },
      { name: "API-Football Audit", href: "/tools/api-football-audit", icon: ClipboardCheck },
      { name: "Teams", href: "/admin-teams", icon: Shield },
      { name: "Leagues", href: "/leagues", icon: Layers3 },
      { name: "Leaderboard", href: "/leaderboard", icon: Trophy },
      { name: "Winners", href: "/winners", icon: Gift },
    ],
  },
  // "quiz-trivia" is built dynamically from the QuizType API — see buildQuizModeGroup below
  {
    id: "daily-challenges",
    label: "Daily Challenges",
    icon: CalendarDays,
    color: "bg-violet-500",
    items: [
      { name: "Manage Challenges", href: "/daily-challenge", icon: Target },
    ],
  },
  {
    id: "academy",
    label: "FBK Academy",
    icon: GraduationCap,
    color: "bg-emerald-600",
    items: [
      { name: "Content", href: "/academy/content", icon: BookOpen },
      { name: "Structure", href: "/academy/structure", icon: Layers },
    ],
  },
  {
    id: "last-fan-standing",
    label: "Last Fan Standing",
    icon: Trophy,
    color: "bg-yellow-500",
    items: [
      { name: "Games & Rounds", href: "/lfs", icon: Shield },
    ],
  },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function loadCollapsed(): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem("sidebar-collapsed") ?? "{}");
  } catch {
    return {};
  }
}

function saveCollapsed(state: Record<string, boolean>) {
  localStorage.setItem("sidebar-collapsed", JSON.stringify(state));
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function Sidebar() {
  const [expanded, setExpanded] = useState(true);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(loadCollapsed);
  const location = useLocation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const navRef = useRef<HTMLElement>(null);

  // Pull live Quiz & Trivia types so admin reorders/renames/toggles are reflected instantly
  const { data: quizTypesData } = useQuizTypesAdmin();
  const quizModeGroup: ModeGroup = {
    id: "quiz-trivia",
    label: "Quiz & Trivia",
    icon: Brain,
    color: "bg-violet-500",
    items: [
      // "Manage Types" is a fixed admin entry
      { name: "Manage Types", href: "/quiz/types", icon: Settings2 },
      ...(quizTypesData?.types ?? [])
        .filter((t) => t.isActive)
        .map((t) => ({
          name: t.name,
          href: t.adminRoute,
          icon: (t.iconKey && ICON_MAP[t.iconKey]) || HelpCircle,
        })),
    ],
  };
  // Insert Quiz & Trivia right after the FIFA World Cup group
  const renderedModeGroups: ModeGroup[] = [
    modeGroups[0],
    quizModeGroup,
    ...modeGroups.slice(1),
  ];

  // Restore sidebar scroll position on mount
  useEffect(() => {
    const saved = sessionStorage.getItem("sidebar-scroll");
    if (saved && navRef.current) navRef.current.scrollTop = Number(saved);
  }, []);

  // Save sidebar scroll position on every scroll
  const handleNavScroll = useCallback(() => {
    if (navRef.current) sessionStorage.setItem("sidebar-scroll", String(navRef.current.scrollTop));
  }, []);

  const toggleMode = useCallback((id: string) => {
    setCollapsed((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      saveCollapsed(next);
      return next;
    });
  }, []);

  const handleLogout = () => {
    clearAllTokens();
    queryClient.clear();
    navigate("/login");
  };

  const isActive = (href: string) =>
    location.pathname === href ||
    (href !== "/" && location.pathname.startsWith(href));

  // When sidebar is collapsed to icon-only, expand on click so items are reachable
  const handleIconCollapse = () => setExpanded(!expanded);

  return (
    <div
      className={cn(
        "flex flex-col h-screen bg-slate-900 transition-all duration-300 shrink-0",
        expanded ? "w-60" : "w-[68px]",
      )}
    >
      {/* Brand header */}
      <div
        className={cn(
          "flex items-center h-16 border-b border-slate-700/60 shrink-0 px-4",
          expanded ? "justify-between" : "justify-center",
        )}
      >
        {expanded && (
          <div className="flex items-center gap-2 min-w-0">
            <img
              src="/logo.png"
              alt="FansBall Knowledge"
              className="w-8 h-8 rounded-lg object-contain shrink-0 bg-white/10"
            />
            <div className="min-w-0">
              <p className="text-white font-bold text-sm leading-tight truncate">
                FansBall Knowledge
              </p>
              <p className="text-slate-400 text-[10px] leading-tight">Admin Console</p>
            </div>
          </div>
        )}
        <button
          onClick={handleIconCollapse}
          className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700/60 transition-colors shrink-0"
        >
          {expanded ? <X size={16} /> : <Menu size={16} />}
        </button>
      </div>

      {/* Scrollable nav */}
      <nav ref={navRef} onScroll={handleNavScroll} className="flex-1 overflow-y-auto py-3 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-slate-600 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-slate-500">

        {/* Static groups */}
        {staticGroups.map((group, gi) => (
          <div key={group.label} className={cn("px-3", gi > 0 && "mt-1")}>
            {expanded && (
              <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest px-2 mb-1 mt-3 first:mt-0">
                {group.label}
              </p>
            )}
            {!expanded && gi > 0 && (
              <div className="border-t border-slate-700/50 my-2" />
            )}
            <ul className="space-y-0.5">
              {group.items.map((item) => (
                <NavLink key={item.name} item={item} active={isActive(item.href)} expanded={expanded} />
              ))}
            </ul>
          </div>
        ))}

        {/* Modes divider */}
        {expanded ? (
          <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest px-5 mb-1 mt-4">
            Modes
          </p>
        ) : (
          <div className="border-t border-slate-700/50 my-3 mx-3" />
        )}

        {/* Mode groups */}
        {renderedModeGroups.map((mode) => {
          const isOpen = !collapsed[mode.id];
          const hasActiveItem = mode.items.some((i) => isActive(i.href));
          const isComingSoon = mode.status === "coming-soon";

          return (
            <div key={mode.id} className="px-3 mb-0.5">
              {/* Mode header button */}
              <button
                onClick={() => !isComingSoon && expanded && toggleMode(mode.id)}
                title={!expanded ? mode.label : undefined}
                className={cn(
                  "w-full flex items-center gap-2.5 px-2 py-2 rounded-lg text-sm transition-all duration-150 group",
                  hasActiveItem
                    ? "text-white"
                    : "text-slate-400 hover:text-slate-200",
                  !isComingSoon && expanded && "hover:bg-slate-700/40 cursor-pointer",
                  isComingSoon && "cursor-default opacity-50",
                  !expanded && "justify-center",
                )}
              >
                {/* Colored mode dot / icon */}
                <div
                  className={cn(
                    "w-5 h-5 rounded flex items-center justify-center shrink-0",
                    mode.color,
                  )}
                >
                  <mode.icon className="h-3 w-3 text-white" />
                </div>

                {expanded && (
                  <>
                    <span className="flex-1 text-left text-xs font-semibold truncate leading-tight">
                      {mode.label}
                    </span>
                    {isComingSoon ? (
                      <span className="text-[9px] font-bold text-slate-500 bg-slate-700/60 px-1.5 py-0.5 rounded-full shrink-0">
                        SOON
                      </span>
                    ) : (
                      <ChevronDown
                        className={cn(
                          "h-3 w-3 shrink-0 text-slate-500 transition-transform duration-200",
                          !isOpen && "-rotate-90",
                        )}
                      />
                    )}
                  </>
                )}
              </button>

              {/* Mode items (only when sidebar expanded + group open + not coming-soon) */}
              {expanded && isOpen && !isComingSoon && mode.items.length > 0 && (
                <ul className="mt-0.5 ml-3.5 pl-2 border-l border-slate-700/50 space-y-0.5">
                  {mode.items.map((item) => (
                    <NavLink
                      key={item.name}
                      item={item}
                      active={isActive(item.href)}
                      expanded={expanded}
                      compact
                    />
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </nav>

      {/* Logout */}
      <div className="shrink-0 p-3 border-t border-slate-700/60">
        <button
          onClick={handleLogout}
          className={cn(
            "w-full flex items-center gap-3 px-2 py-2 rounded-lg text-sm text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors duration-150",
            !expanded && "justify-center",
          )}
          title={!expanded ? "Logout" : undefined}
        >
          <LogOut className="h-4 w-4 shrink-0" />
          {expanded && <span>Logout</span>}
        </button>
      </div>
    </div>
  );
}

// ─── NavLink ─────────────────────────────────────────────────────────────────

function NavLink({
  item,
  active,
  expanded,
  compact = false,
}: {
  item: NavItem;
  active: boolean;
  expanded: boolean;
  compact?: boolean;
}) {
  const inner = (
    <div
      className={cn(
        "flex items-center gap-3 px-2 rounded-lg text-sm transition-all duration-150 group relative",
        compact ? "py-1.5" : "py-2",
        active
          ? "bg-blue-600/20 text-blue-400 font-medium"
          : "text-slate-400 hover:bg-slate-700/50 hover:text-slate-200",
      )}
    >
      {active && (
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-4 bg-blue-500 rounded-r" />
      )}
      <item.icon
        className={cn(
          "shrink-0",
          compact ? "h-3.5 w-3.5" : "h-4 w-4",
          active ? "text-blue-400" : "text-slate-500 group-hover:text-slate-300",
        )}
      />
      {expanded && <span className="truncate flex-1">{item.name}</span>}
      {expanded && active && (
        <ChevronRight className="h-3 w-3 text-blue-400/60 shrink-0" />
      )}
    </div>
  );

  if (item.external) {
    return (
      <li>
        <a
          href={item.href}
          target="_blank"
          rel="noopener noreferrer"
          title={!expanded ? item.name : undefined}
        >
          {inner}
        </a>
      </li>
    );
  }

  return (
    <li>
      <Link to={item.href} title={!expanded ? item.name : undefined} preventScrollReset>
        {inner}
      </Link>
    </li>
  );
}
